/**
 * ECA Triage Accelerator - Main Application
 *
 * IMPORTANT: This file is the login boot path. Only import modules needed
 * for public routes (login, signup, waitlist, benchmarks). All authenticated
 * route dependencies (Layout, WorkflowContext, GlobalIngestionOverlay, etc.)
 * live in AuthenticatedApp.tsx which is lazy-loaded. This prevents any
 * circular-import TDZ errors in the authenticated module graph from crashing
 * public pages like /login.
 */

import { lazy, Suspense } from "react"
import { BrowserRouter as Router, Routes, Route } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Toaster } from "sonner"
import { AuthProvider } from "@/contexts/AuthContext"
import { ThemeProvider, useTheme } from "@/components/theme-provider"
import { UserPreferencesProvider } from "@/contexts/UserPreferencesContext"
import { ErrorBoundary } from "@/components/ErrorBoundary"

// Public pages - eagerly loaded (lightweight, no Layout deps)
import LoginPage from "@/pages/LoginPage"

// Lazy-loaded public pages
const SignupPage = lazy(() => import("@/pages/SignupPage"))
const LiteEntryPage = lazy(() => import("@/pages/LiteEntryPage"))
const BenchmarksPage = lazy(() => import("@/pages/BenchmarksPage"))
const WaitlistPage = lazy(() => import("@/pages/WaitlistPage"))
const SharedBriefPage = lazy(() => import("@/pages/SharedBriefPage"))

// Lazy-loaded authenticated app shell (Layout, WorkflowContext, all auth routes)
const AuthenticatedApp = lazy(() => import("@/components/AuthenticatedApp"))

// Agentation dev toolbar - lazy so it never blocks boot
const Agentation = lazy(() => import("agentation").then(m => ({ default: m.Agentation })))

// ---------------------------------------------------------------------------
// Shared loading fallback
// ---------------------------------------------------------------------------

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Query client
// ---------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Number(import.meta.env.VITE_DEFAULT_STALE_TIME_MS) || 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// ---------------------------------------------------------------------------
// App shell - minimal for fast /login boot
// ---------------------------------------------------------------------------

function AppShell() {
  const { resolvedColorMode } = useTheme()
  const showAgentation =
    import.meta.env.DEV && import.meta.env.VITE_ENABLE_AGENTATION === "true"

  return (
    <div className="min-h-screen bg-background antialiased">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes - no heavy deps, no Layout */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/lite" element={<LiteEntryPage />} />
          <Route path="/lite/login" element={<LoginPage entryMode="lite" />} />
          <Route path="/lite/signup" element={<SignupPage entryMode="lite" />} />
          <Route path="/benchmarks" element={<BenchmarksPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/share/:token" element={<SharedBriefPage />} />

          {/* Everything else - lazy-loaded authenticated shell */}
          <Route path="/*" element={<AuthenticatedApp />} />
        </Routes>
      </Suspense>
      <Toaster position="bottom-right" richColors theme={resolvedColorMode} toastOptions={{ className: "rounded-lg font-sans" }} />
      {showAgentation && (
        <Suspense fallback={null}>
          <Agentation endpoint={import.meta.env.VITE_AGENTATION_URL || "http://localhost:4747"} />
        </Suspense>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

function App() {
  return (
    <ErrorBoundary label="Application">
      <QueryClientProvider client={queryClient}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <UserPreferencesProvider>
              <ThemeProvider defaultTheme="system">
                <AppShell />
              </ThemeProvider>
            </UserPreferencesProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
